import { Navigate, Route, Routes, Link, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  LayoutDashboard,
  Settings,
  Users,
  Bug,
  FileText,
  BarChart3,
  LogOut,
  Menu,
  X,
  type LucideIcon,
} from 'lucide-react'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import Home from './pages/Home'
import ServicesPage from './pages/admin/ServicesPage'
import LogsPage from './pages/admin/LogsPage'
import AnalyticsPage from './pages/admin/AnalyticsPage'
import ApiDebugPage from './pages/admin/ApiDebugPage'
import UsersPage from './pages/admin/UsersPage'

function AppLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [adminUser, setAdminUser] = useState<any>(null)

  useEffect(() => {
    const user = localStorage.getItem('admin_user')
    if (user) {
      setAdminUser(JSON.parse(user))
    }
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_user')
    window.location.href = '/login'
  }

  const navItems: Array<{ path: string; label: string; icon: LucideIcon }> = [
    { path: '/', label: '概览', icon: LayoutDashboard },
    { path: '/admin/services', label: '服务管理', icon: Settings },
    { path: '/admin/users', label: '用户管理', icon: Users },
    { path: '/admin/api-debug', label: 'API调试', icon: Bug },
    { path: '/admin/logs', label: '调用日志', icon: FileText },
    { path: '/admin/analytics', label: '数据分析', icon: BarChart3 },
  ]

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* 桌面端侧边栏 */}
      <aside className="hidden md:flex md:w-64 md:flex-col bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white shadow-2xl">
        {/* Logo 区域 */}
        <div className="flex h-16 items-center border-b border-slate-700/50 px-6">
          <div className="flex items-center gap-3">
            <img
              src="https://flowspark.net/favicon.ico"
              alt="Logo"
              className="w-10 h-10 rounded-lg"
            />
            <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
              API Adapter
            </h1>
          </div>
        </div>

        {/* 导航菜单 */}
        <nav className="flex-1 space-y-1 p-4">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path
            const Icon = item.icon
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/50'
                    : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            )
          })}
        </nav>

        {/* 底部用户区域 */}
        <div className="border-t border-slate-700/50 p-4">
          <div className="mb-3 px-3">
            <p className="text-sm font-medium text-white truncate">
              {adminUser?.user_metadata?.name || '管理员'}
            </p>
            <p className="text-xs text-slate-400 truncate">{adminUser?.email || ''}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-start gap-3 rounded-xl px-4 py-3 text-sm font-medium text-slate-300 hover:bg-red-600/20 hover:text-red-400 transition-all duration-200"
          >
            <LogOut className="w-4 h-4" />
            <span>退出登录</span>
          </button>
        </div>
      </aside>

      {/* 移动端顶部栏 */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 flex h-16 items-center justify-between border-b bg-white/95 backdrop-blur-sm px-4 shadow-sm">
        <div className="flex items-center gap-2">
          <img
            src="https://flowspark.net/favicon.ico"
            alt="Logo"
            className="w-8 h-8 rounded-lg"
          />
          <h1 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            API Adapter
          </h1>
        </div>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          {isMobileMenuOpen ? (
            <X className="w-5 h-5" />
          ) : (
            <Menu className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* 移动端菜单 */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 pt-16">
          <nav className="space-y-1 p-4">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path
              const Icon = item.icon
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                      : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </nav>
          <div className="border-t border-slate-700/50 p-4 mt-4">
            <div className="mb-3 px-3">
              <p className="text-sm font-medium text-white">
                {adminUser?.user_metadata?.name || '管理员'}
              </p>
              <p className="text-xs text-slate-400">{adminUser?.email || ''}</p>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-start gap-3 rounded-xl px-4 py-3 text-sm font-medium text-slate-300 hover:bg-red-600/20 hover:text-red-400 transition-all"
            >
              <LogOut className="w-4 h-4" />
              <span>退出登录</span>
            </button>
          </div>
        </div>
      )}

      {/* 主内容区 */}
      <main className="flex-1 md:pt-0 pt-16 overflow-auto">
        {children}
      </main>
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('admin_token')
  return token ? <>{children}</> : <Navigate to="/login" replace />
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/sys/admin-init" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Home />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/services"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ServicesPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/api-debug"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ApiDebugPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/logs"
        element={
          <ProtectedRoute>
            <AppLayout>
              <LogsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute>
            <AppLayout>
              <AnalyticsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AppLayout>
              <UsersPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/admin" element={<Navigate to="/admin/services" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
